import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { DropdownObject } from '@inmoment/the-kitchen';
import {
  RGBAColor,
  RGBAColorInput,
} from '@inmoment/the-kitchen/src/lib/Pickers/ColorPicker/ColorPicker.model';

import { ChartOptionConstants, Constants } from 'app/constants/constants';
import { DEFAULT_PROJECT_COLOR_PALETTE } from '../../../../providers/storage-api/project.service';
import { ChartCustomColorService } from '../../../dashboard/services/chart-custom-color.service';

type TopBoxSize = '1' | '2' | '3';
type BottomBoxSize = '1' | '2' | '3' | '4' | '5' | '6' | '7';

export interface BoxScoringDisplayData {
  isTopShown: boolean;
  isNeutralShown: boolean;
  isBottomShown: boolean;
  topBoxSize: TopBoxSize;
  bottomBoxSize: BottomBoxSize;
  topColor: RGBAColorInput;
  neutralColor: RGBAColorInput;
  bottomColor: RGBAColorInput;
  defaultColorPalette?: RGBAColor[];
}

export class BoxScoringDisplayDataUtils {
  public static getObjectDefaults(): BoxScoringDisplayData {
    return {
      isTopShown: true,
      isNeutralShown: true,
      isBottomShown: true,
      topBoxSize: Constants.TWTRA_DEFAULT_BOX_SIZE,
      bottomBoxSize: Constants.TWTRA_DEFAULT_BOX_SIZE,
      topColor: { red: 85, green: 191, blue: 59, alpha: 1 },
      neutralColor: { red: 146, green: 146, blue: 146, alpha: 1 },
      bottomColor: { red: 223, green: 83, blue: 83, alpha: 1 },
    };
  }
}

@Component({
  selector: 'app-box-scoring-display',
  templateUrl: './box-scoring-display.component.html',
  styleUrls: ['./box-scoring-display.component.scss'],
  standalone: false,
})
export class BoxScoringDisplayComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data: BoxScoringDisplayData;
  @Input() disabled?: boolean;

  @Output() update: EventEmitter<BoxScoringDisplayData> = new EventEmitter<BoxScoringDisplayData>();

  readonly topBoxSet: DropdownObject[] = ChartOptionConstants.TOPBOXSIZEDOS;
  readonly bottomBoxSet: DropdownObject[] = ChartOptionConstants.BOTTOMBOXSIZEDOS;

  isTopShown: boolean;
  isNeutralShown: boolean;
  isBottomShown: boolean;
  topBoxSize: TopBoxSize;
  bottomBoxSize: BottomBoxSize;
  topColor: RGBAColorInput;
  neutralColor: RGBAColorInput;
  bottomColor: RGBAColorInput;
  projectPalette: { color: RGBAColor }[] = DEFAULT_PROJECT_COLOR_PALETTE.map((colorString) => ({
    color: ChartCustomColorService.colorStringToRgbaColor(colorString),
  }));

  update$: Subject<void> = new Subject<void>();
  private updateUnsub$: Subject<void> = new Subject<void>();

  constructor() {
    this.update$.pipe(debounceTime(100), takeUntil(this.updateUnsub$)).subscribe(() => this.emit());
  }

  ngOnInit(): void {
    this.isTopShown = this.data.isTopShown;
    this.isNeutralShown = this.data.isNeutralShown;
    this.isBottomShown = this.data.isBottomShown;
    this.topBoxSize = this.data.topBoxSize;
    this.bottomBoxSize = this.data.bottomBoxSize;
    this.topColor = this.data.topColor;
    this.neutralColor = this.data.neutralColor;
    this.bottomColor = this.data.bottomColor;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data && this.data?.defaultColorPalette) {
      this.projectPalette = this.data.defaultColorPalette.map((color) => ({ color }));
    }
  }

  ngOnDestroy(): void {
    this.updateUnsub$.next();
    this.updateUnsub$.complete();
  }

  onDisplayChanged(type: 'top' | 'neutral' | 'bottom', value: boolean): void {
    switch (type) {
      case 'top':
        this.isTopShown = value;
        break;
      case 'neutral':
        this.isNeutralShown = value;
        break;
      case 'bottom':
        this.isBottomShown = value;
        break;
    }

    this.update$.next();
  }

  onTopBoxSizeChanged(value: TopBoxSize): void {
    this.topBoxSize = value;

    this.update$.next();
  }

  onBottomBoxSizeChanged(value: BottomBoxSize): void {
    this.bottomBoxSize = value;

    this.update$.next();
  }

  private emit(): void {
    this.update.emit({
      isTopShown: this.isTopShown,
      isNeutralShown: this.isNeutralShown,
      isBottomShown: this.isBottomShown,
      topBoxSize: this.topBoxSize,
      bottomBoxSize: this.bottomBoxSize,
      topColor: this.topColor,
      neutralColor: this.neutralColor,
      bottomColor: this.bottomColor,
    });
  }
}
